import {
    CarUICreateForm,
    makeCarEmptyUICreateForm,
} from '@/types/car/car-ui-create-form.type.ts';
import { CarUpdateForm, CarUpdateFormPhoto } from '@/types/car/car-update-form.type.ts';
import { Car } from '@/types/car/car.type.ts';
import {
    ComplectationOption,
    makeComplectationOptionsEmptyUICreateForm,
    mapComplectationOptionsUICreateFormToRequestCreateForm,
    complectationOptionRawValueToUIFormValue,
} from '@/types/complectation.type.ts';
import { CarDamageCreateForm } from '@/types/car/car-damage-create-form.type.ts';
import {
    CarCreateFormDocuments,
    CarCreateFormPhoto,
    CarCreateFormPhotos,
} from '@/types/car/car-create-form.type.ts';
import { CarDamageUpdateForm } from '@/types/car/car-damage-update-form.type.ts';
import { CarPhotoType } from '@/types/car/car-photo-type.type.ts';

export type CarUIUpdateForm = CarUICreateForm & {
    new_car_damages: CarDamageCreateForm[],
    new_photos: CarCreateFormPhotos,
    new_additional_photos: CarCreateFormPhoto[],
    new_documents: CarCreateFormDocuments,
    new_car_videos: File[],
    updated_car_damages: CarDamageUpdateForm[],
    deleted_car_damages: number[],
    deleted_photos: number[],
    deleted_additional_photos: number[],
    deleted_documents: number[],
    deleted_videos: number[],
    updated_photos: CarUpdateFormPhoto[],
    updated_additional_photos: CarUpdateFormPhoto[],
};

export function makeCarEmptyUIUpdateForm(): CarUIUpdateForm {
    return {
        ...makeCarEmptyUICreateForm(),
        ...{
            new_car_damages: [],
            new_photos: {},
            new_additional_photos: [],
            new_documents: {},
            new_car_videos: [],
            updated_car_damages: [],
            deleted_car_damages: [],
            deleted_photos: [],
            deleted_additional_photos: [],
            deleted_documents: [],
            deleted_videos: [],
            updated_photos: [],
            updated_additional_photos: [],
        },
    };
}

export function mapCarUIUpdateFormToRequestUpdateForm(uiUpdateForm: CarUIUpdateForm): CarUpdateForm {
    const updateForm: CarUpdateForm = {
        vin: uiUpdateForm.vin,
        pts_number: uiUpdateForm.pts_number,
        body_color: uiUpdateForm.body_color,
        is_stolen: uiUpdateForm.is_not_stolen ? 0 : 1,
        is_pledged: uiUpdateForm.is_not_pledged ? 0 : 1,
        exist_dtp: uiUpdateForm.not_exist_dtp ? 0 : 1,
        was_owned_by_individual: uiUpdateForm.was_owned_by_individual ? 1 : 0,
        options: mapComplectationOptionsUICreateFormToRequestCreateForm(uiUpdateForm.options),
        new_car_damages: uiUpdateForm.new_car_damages,
        new_photos: {},
        new_additional_photos: [],
        new_documents: uiUpdateForm.new_documents,
        new_car_videos: uiUpdateForm.new_car_videos,
        updated_car_damages: uiUpdateForm.updated_car_damages,
        deleted_car_damages: uiUpdateForm.deleted_car_damages,
        deleted_photos: uiUpdateForm.deleted_photos,
        deleted_additional_photos: uiUpdateForm.deleted_additional_photos,
        deleted_documents: uiUpdateForm.deleted_documents,
        deleted_videos: uiUpdateForm.deleted_videos,
        updated_photos: uiUpdateForm.updated_photos,
        updated_additional_photos: uiUpdateForm.updated_additional_photos,
    };

    if (uiUpdateForm.brand_id !== undefined) {
        updateForm.brand_id = uiUpdateForm.brand_id;
    }

    if (uiUpdateForm.model_id !== undefined) {
        updateForm.model_id = uiUpdateForm.model_id;
    }

    if (uiUpdateForm.model_code_id !== undefined) {
        updateForm.model_code_id = uiUpdateForm.model_code_id;
    }

    if (uiUpdateForm.admission_type !== undefined) {
        updateForm.admission_type = uiUpdateForm.admission_type;
    }

    if (uiUpdateForm.body_type !== undefined) {
        updateForm.body_type = uiUpdateForm.body_type;
    }

    if (uiUpdateForm.engine_type !== undefined) {
        updateForm.engine_type = uiUpdateForm.engine_type;
    }

    if (uiUpdateForm.drive_type !== undefined) {
        updateForm.drive_type = uiUpdateForm.drive_type;
    }

    if (uiUpdateForm.mileage !== undefined && uiUpdateForm.mileage.length > 0) {
        updateForm.mileage = parseInt(uiUpdateForm.mileage);
    }

    if (uiUpdateForm.production_year !== undefined) {
        updateForm.production_year = uiUpdateForm.production_year;
    }

    if (uiUpdateForm.engine_power !== undefined && uiUpdateForm.engine_power.length > 0) {
        updateForm.engine_power = parseInt(uiUpdateForm.engine_power);
    }

    if (uiUpdateForm.engine_volume !== undefined) {
        updateForm.engine_volume = uiUpdateForm.engine_volume;
    }

    if (uiUpdateForm.start_price !== undefined && uiUpdateForm.start_price.length > 0) {
        updateForm.start_price = parseInt(uiUpdateForm.start_price);
    }

    if (uiUpdateForm.nds_type !== undefined) {
        updateForm.nds_type = uiUpdateForm.nds_type;
    }

    if (uiUpdateForm.transmission_type !== undefined) {
        updateForm.transmission_type = uiUpdateForm.transmission_type;
    }

    if (uiUpdateForm.additional_equipment !== undefined && uiUpdateForm.additional_equipment.length > 0) {
        updateForm.additional_equipment = uiUpdateForm.additional_equipment;
    }

    if (uiUpdateForm.dealer_id !== undefined) {
        updateForm.dealer_id = uiUpdateForm.dealer_id;
    }

    if (uiUpdateForm.complectation_id !== undefined) {
        updateForm.complectation_id = uiUpdateForm.complectation_id;
    }

    if (uiUpdateForm.owners_count !== undefined && Number(uiUpdateForm.owners_count)) {
        updateForm.owners_count = Number(uiUpdateForm.owners_count);
    }

    if (uiUpdateForm.months_with_last_owner !== undefined && Number(uiUpdateForm.months_with_last_owner)) {
        updateForm.months_with_last_owner = Number(uiUpdateForm.months_with_last_owner);
    }

    if (uiUpdateForm.comment !== undefined && uiUpdateForm.comment.length > 0) {
        updateForm.comment = uiUpdateForm.comment;
    }

    if (uiUpdateForm.tags !== undefined && uiUpdateForm.tags.length > 0) {
        updateForm.tags = uiUpdateForm.tags;
    }

    const photos: CarCreateFormPhotos = {};
    Object.entries(uiUpdateForm.new_photos).forEach(([type, photo]) => {
        if (photo.file !== undefined && photo.file !== null) {
            photos[type as CarPhotoType] = { ...photo };
        }
    });

    if (Object.keys(photos).length > 0) {
        updateForm.new_photos = photos;
    }

    const additionalPhotos: CarCreateFormPhoto[] = [];
    uiUpdateForm.new_additional_photos.forEach((photo) => {
        if (photo.file !== undefined && photo.file !== null) {
            additionalPhotos.push({ ...photo });
        }
    });

    if (additionalPhotos.length > 0) {
        updateForm.new_additional_photos = additionalPhotos;
    }

    return updateForm;
}

export function fillCarUIUpdateFormFromCar(car: Car): CarUIUpdateForm {
    const form: CarUIUpdateForm = makeCarEmptyUIUpdateForm();

    form.brand_id = car.brand.id;
    form.model_id = car.model.id;

    if (car.model_code) {
        form.model_code_id = car.model_code.id;
    }

    form.vin = car.vin;
    form.admission_type = car.admission_type;
    form.pts_number = car.pts_number;
    form.body_type = car.body_type;
    form.body_color = car.body_color;
    form.engine_type = car.engine_type;
    form.drive_type = car.drive_type;
    form.mileage = `${car.mileage}`;
    form.production_year = car.production_year;
    form.engine_power = `${car.engine_power}`;
    form.engine_volume = car.engine_volume;
    form.start_price = `${car.start_price}`;
    form.nds_type = car.nds_type;
    form.transmission_type = car.transmission_type;
    form.additional_equipment = car.additional_equipment ?? undefined;
    form.dealer_id = car.dealer.id ?? undefined;
    form.complectation_id = car.complectation.id ?? undefined;
    form.owners_count = car.owners_count ?? undefined;
    form.months_with_last_owner = car.months_with_last_owner ?? undefined;
    form.is_not_stolen = !car.is_stolen;
    form.is_not_pledged = !car.is_pledged;
    form.not_exist_dtp = !car.exist_dtp;
    form.comment = car.comment ?? '';
    form.tags = car.tags.join(', ');
    form.was_owned_by_individual = car.was_owned_by_individual ?? false;

    const options: Record<string, string | boolean> = {};
    car.options?.forEach((option: ComplectationOption) => {
        options[option.option_symbolic_code] = complectationOptionRawValueToUIFormValue(
            option.option_symbolic_code,
            option.option_value_symbolic_code,
        );
    });
    form.options = { ...makeComplectationOptionsEmptyUICreateForm(), ...options };

    return form;
}
